import { Theme } from '@savgroup-front-common/types';

import { BRAND_COLOR_VARIABLE_BY_COLOR_KEY } from '../brandTokens';

export type ThemeColorOverride = Partial<Theme['colors']>;

export interface ThemeOverride {
  colors?: ThemeColorOverride;
}

/**
 * Reads the `--brand-*` CSS custom properties currently resolved on `:root` and
 * shapes them back into a styled-components compatible theme override
 * (`{ colors: { ... } }`).
 *
 * This is the return path of the theming indirection layer (gap G1): it lets the
 * legacy styled-components theme consume whatever the Tailwind design system —
 * or a seller override applied through `applyThemeToCSSVariables` — resolved for
 * those tokens, so both systems read a single source of truth.
 */
export const adaptCssVariablesToThemeOverride = (): ThemeOverride => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {};
  }

  const computedStyle = window.getComputedStyle(document.documentElement);

  const colors = Object.entries(BRAND_COLOR_VARIABLE_BY_COLOR_KEY).reduce<ThemeColorOverride>(
    (accumulator, [colorKey, cssVariable]) => {
      const value = computedStyle.getPropertyValue(cssVariable).trim();

      if (!value) {
        return accumulator;
      }

      return {
        ...accumulator,
        [colorKey]: value,
      };
    },
    {},
  );

  if (Object.keys(colors).length === 0) {
    return {};
  }

  return { colors };
};
